import { useCallback, useEffect, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Download, RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePOS } from "@/context/POSContext";
import { businessDateFor } from "@/lib/interactionLog/businessDate";
import { downloadDayFromServer } from "@/lib/interactionLog/downloads";
import { timeAgo } from "@/pages/LoggingPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Phones upload every 3 minutes while open. Past this, a device is either closed or
// failing to upload — either way it is worth seeing.
const STALE_MS = 15 * 60 * 1000;
const REFRESH_MS = 30 * 1000;

function describeAgent(userAgent) {
  if (!userAgent) return "";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad/i.test(userAgent)) return "iOS";
  if (/Windows/i.test(userAgent)) return "Windows";
  if (/Mac OS/i.test(userAgent)) return "Mac";
  if (/Linux/i.test(userAgent)) return "Linux";
  return "";
}

/**
 * Admin → Device Logs: who is uploading, who has gone quiet, a force-sync, and
 * date-wise downloads of everything the server holds (phones and server together).
 */
export default function DeviceLogsPanel() {
  const { token } = usePOS();
  const [status, setStatus] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [forcing, setForcing] = useState(false);
  const [forceResult, setForceResult] = useState(null);
  const [day, setDay] = useState(() => businessDateFor());
  const [deviceFilter, setDeviceFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statusResponse, devicesResponse] = await Promise.all([
        axios.get(`${API}/logs/status`, { headers }),
        axios.get(`${API}/logs/devices`, { headers }),
      ]);
      setStatus(statusResponse.data);
      setDevices(Array.isArray(devicesResponse.data) ? devicesResponse.data : []);
      setLoadError(null);
    } catch (error) {
      const code = error?.response?.status;
      setLoadError(code === 403 ? "Your account cannot view device logs." : code ? `The server answered ${code}.` : "The server could not be reached.");
    }
  }, [token]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const forceSync = async () => {
    setForcing(true);
    try {
      const { data } = await axios.post(`${API}/logs/force-sync`, null, { headers: { Authorization: `Bearer ${token}` } });
      const reached = data.connected_sockets;
      setForceResult({ reached, at: data.requested_at });
      toast.success(`Asked ${reached} connected ${reached === 1 ? "terminal" : "terminals"} to upload`);
      setTimeout(load, 8000);
    } catch (error) {
      toast.error(error?.response?.status === 403 ? "Your account cannot force a sync." : "Force sync failed — the server could not be reached.");
    } finally {
      setForcing(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const bytes = await downloadDayFromServer({ businessDate: day, deviceId: deviceFilter || null, source: sourceFilter || null });
      if (!bytes) toast.info("No logs for that day and filter.");
    } catch (error) {
      toast.error(error?.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="device-logs-panel">
      {loadError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-body text-destructive" data-testid="device-logs-error">
          {loadError}
        </div>
      )}

      {status && (
        <div className="grid grid-cols-2 gap-3 rounded-md border bg-card p-3 text-micro sm:grid-cols-4" data-testid="device-logs-status">
          <div>
            <div className="text-muted-foreground">Server logging</div>
            <div className="font-semibold">{status.enabled ? "On" : "Off"}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Kept for</div>
            <div className="font-semibold">{status.retention_days} days (since {status.oldest_kept})</div>
          </div>
          <div>
            <div className="text-muted-foreground">Business day</div>
            <div className="font-semibold">{status.business_date} · turns at {status.business_day_start_hour}:00</div>
          </div>
          <div>
            <div className="text-muted-foreground">Server events</div>
            <div className="font-semibold">{status.server_events_written} written</div>
          </div>
          {status.server_events_dropped > 0 && (
            <div role="alert" className="col-span-full flex gap-2 rounded bg-warning/10 p-2 text-warning" data-testid="device-logs-dropped">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {status.server_events_dropped} server events could not be saved since the server started. Requests were not affected.
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-card p-3">
        <div className="min-w-0">
          <div className="text-body font-bold">Force sync</div>
          <p className="text-micro text-muted-foreground">
            Asks every open terminal to upload now. Closed or offline phones upload by themselves when they next open.
          </p>
          {forceResult && (
            <p className="text-micro font-semibold" data-testid="device-logs-force-result">
              Reached {forceResult.reached} connected {forceResult.reached === 1 ? "terminal" : "terminals"} {timeAgo(forceResult.at)}.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="device-logs-refresh">
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="action" size="sm" onClick={forceSync} disabled={forcing} data-testid="device-logs-force-sync">
            <Send className="mr-1 h-4 w-4" />
            {forcing ? "Asking…" : "Force sync all"}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border bg-card" data-testid="device-logs-table">
        <table className="w-full text-micro">
          <thead className="border-b text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Staff</th>
              <th className="px-3 py-2">Last upload</th>
              <th className="px-3 py-2">Waiting</th>
              <th className="px-3 py-2">Today</th>
              <th className="px-3 py-2">Logging</th>
              <th className="px-3 py-2">App</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground" data-testid="device-logs-empty">
                  No device has uploaded logs yet.
                </td>
              </tr>
            ) : (
              devices.map(device => {
                const stale = !device.last_synced_at || Date.now() - new Date(device.last_synced_at).getTime() > STALE_MS;
                return (
                  <tr key={device.device_id} className="border-b last:border-0" data-testid={`device-logs-row-${device.device_id}`}>
                    <td className="px-3 py-2 font-mono" title={device.device_id}>
                      {device.device_id.slice(0, 8)} <span className="font-sans text-muted-foreground">{describeAgent(device.user_agent)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {device.employee_id || "—"} <span className="text-muted-foreground">{device.role}</span>
                    </td>
                    <td className={`px-3 py-2 ${stale ? "font-semibold text-warning" : ""}`} data-testid="device-logs-last-sync">
                      {timeAgo(device.last_synced_at) || "never"}
                    </td>
                    <td className={`px-3 py-2 ${device.pending_reported ? "font-semibold" : ""}`}>{device.pending_reported ?? "—"}</td>
                    <td className="px-3 py-2">{device.entries_today}</td>
                    <td className="px-3 py-2">
                      {device.logging_enabled === false ? (
                        <span className="font-semibold text-destructive" data-testid="device-logs-logging-off">Off</span>
                      ) : (
                        "On"
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono">{device.app_version || "—"}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-md border bg-card p-3" data-testid="device-logs-download">
        <label className="flex flex-col text-micro">
          <span className="text-muted-foreground">Business day</span>
          <DatePicker value={day} onChange={setDay} ariaLabel="Business day" testId="device-logs-day" />
        </label>
        <label className="flex flex-col text-micro">
          <span className="text-muted-foreground">Device</span>
          <select value={deviceFilter} onChange={event => setDeviceFilter(event.target.value)} className="rounded border bg-background px-2 py-1" data-testid="device-logs-device">
            <option value="">All devices</option>
            {devices.map(device => (
              <option key={device.device_id} value={device.device_id}>
                {device.device_id.slice(0, 8)} {device.employee_id ? `· ${device.employee_id}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-micro">
          <span className="text-muted-foreground">Source</span>
          <select value={sourceFilter} onChange={event => setSourceFilter(event.target.value)} className="rounded border bg-background px-2 py-1" data-testid="device-logs-source">
            <option value="">Devices and server</option>
            <option value="client">Devices only</option>
            <option value="server">Server only</option>
          </select>
        </label>
        <Button variant="outline" size="sm" onClick={download} disabled={downloading || !day} data-testid="device-logs-download-button">
          <Download className="mr-1 h-4 w-4" />
          {downloading ? "Preparing…" : "Download"}
        </Button>
      </div>
    </div>
  );
}
